/* eslint-disable import/no-cycle */
/* eslint-disable no-param-reassign */

import decode from 'jwt-decode';
import _ from 'lodash';
import appClient from './api/app.client';
import apiClient from './api/api.client';
import DealModel from '../models/deal';
import CustomerModel from '../models/customer';
import DocumentModel from '../models/document';
import DocumentRequestModel from '../models/documentRequest';
import RegistrationModel from '../models/registration';
import RegistrationTokenModel from '../models/registrationToken';

class ApiService {
  constructor() {
    this.apiClient = apiClient;
    this.appClient = appClient;
  }

  fetchRecords(aPage) {
    const app = this;
    const page = aPage || 1;

    return new Promise((resolve, reject) => {
      app.apiClient.get('/customer-deals', {
        params: {
          page,
        },
      }).then((response) => {
        response.data.data = _.map(response.data.data, (record) => {
          record.customer = new CustomerModel(record.customer);

          record.document_requests = _.map(
            record.document_requests,
            (request) => new DocumentRequestModel(request),
          );

          record.documents = _.map(
            record.documents,
            (document) => new DocumentModel(document),
          );

          return new DealModel(record);
        });
        resolve(response);
      }).catch((error) => {
        reject(app.constructor.formatError(error));
      });
    });
  }

  fetchRecord(recordId) {
    const app = this;
    return new Promise((resolve, reject) => {
      app.apiClient.get(`/customer-deals/${recordId}`)
        .then((response) => {
          response.data.customer = new CustomerModel(response.data.customer);

          response.data.document_requests = _.map(
            response.data.document_requests,
            (request) => new DocumentRequestModel(request),
          );

          response.data.documents = _.map(
            response.data.documents,
            (document) => new DocumentModel(document),
          );

          resolve(
            new DealModel(response.data),
          );
        }).catch((error) => {
          reject(app.constructor.formatError(error));
        });
    });
  }

  fetchRequests(complete = false) {
    const app = this;
    return new Promise((resolve, reject) => {
      app.apiClient.get(`/customer-deals/document-requests?complete=${complete}`)
        .then((response) => {
          const requests = _.map(
            response.data.data,
            (request) => {
              request.documents = _.map(
                request.documents,
                (document) => new DocumentModel(document),
              );
              return new DocumentRequestModel(request);
            },
          );

          resolve(requests);
        }).catch((error) => {
          reject(app.constructor.formatError(error));
        });
    });
  }

  fetchCustomer(customerId) {
    const app = this;
    return new Promise((resolve, reject) => {
      this.appClient.get(`/customer/${customerId}`)
        .then((response) => {
          resolve(new CustomerModel(response.data));
        }).catch((error) => {
          reject(app.constructor.formatError(error));
        });
    });
  }

  updateCustomerModel(customer) {
    const app = this;
    return new Promise((resolve, reject) => {
      this.appClient.patch(`/customer/${customer.id}`, customer)
        .then((response) => {
          resolve(new CustomerModel(response.data));
        }).catch((error) => {
          reject(app.constructor.formatError(error));
        });
    });
  }

  saveContactPreferences(customer) {
    const app = this;
    return new Promise((resolve, reject) => {
      this.appClient.patch(`/customer/${customer.id}/contact-preferences`, {
        contactable_sms: customer.contactable_sms,
        contactable_email: customer.contactable_email,
        contactable_phone: customer.contactable_phone,
        contactable_post: customer.contactable_post,
      }).then((response) => {
        resolve(new CustomerModel(Object.assign(customer, response.data)));
      }).catch((error) => {
        reject(app.constructor.formatError(error));
      });
    });
  }

  /**
   * @param {RegistrationModel} registrationModel
   * @returns {Promise<any>}
   */
  updateRegistrationModel(registrationModel) {
    const app = this;
    return new Promise((resolve, reject) => {
      let url = '';
      if (registrationModel.group_slug) {
        url += `/${registrationModel.group_slug}`;
      }
      url += '/register';

      this.appClient.post(url, registrationModel)
        .then((response) => {
          const newRegistrationModel = new RegistrationModel(response.data);
          newRegistrationModel.setDealerSlug(registrationModel.group_slug);
          resolve(newRegistrationModel);
        }).catch((error) => {
          reject(app.constructor.formatError(error));
        });
    });
  }

  /**
   * @param {RegistrationTokenModel} tokenModel
   * @returns {Promise<any>}
   */
  checkRegistrationToken(tokenModel) {
    const app = this;
    return new Promise((resolve, reject) => {
      this.appClient.post(`/${tokenModel.group_slug}/check-token`, tokenModel)
        .then((response) => {
          const newTokenModel = new RegistrationTokenModel(response.data);
          newTokenModel.setDealerSlug(tokenModel.group_slug);
          resolve(newTokenModel);
        }).catch((error) => {
          reject(app.constructor.formatError(error));
        });
    });
  }

  documentRequestReply(dealId, requestId, data) {
    const app = this;
    return new Promise((resolve, reject) => {
      app.apiClient.post(`/customer-deals/${dealId}/document-requests/${requestId}/reply`, data)
        .then((response) => {
          resolve(new DocumentRequestModel(response.data));
        }).catch((error) => {
          reject(app.constructor.formatError(error));
        });
    });
  }

  getAddress(postcode) {
    const app = this;
    return new Promise((resolve, reject) => {
      app.appClient.post(`/postcode/${postcode}`)
        .then((response) => {
          resolve(response);
        }).catch((error) => {
          reject(app.constructor.formatError(error));
        });
    });
  }

  requestDelete(dealId) {
    const app = this;
    return new Promise((resolve, reject) => {
      app.apiClient.post(`/customer-deals/${dealId}/suppression-request`)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(app.constructor.formatError(error));
        });
    });
  }

  updatePassword(newPassword, oldPassword) {
    const app = this;
    return new Promise((resolve, reject) => {
      this.appClient.post('auth/update-password', {
        new_password: newPassword,
        old_password: oldPassword,
      }).then((response) => {
        resolve(response);
      }).catch((error) => {
        reject(app.constructor.formatError(error));
      });
    });
  }

  fetchDocument(dealId, documentId) {
    const app = this;
    return new Promise((resolve, reject) => {
      this.apiClient.get(`/customer-deals/${dealId}/documents/${documentId}/download/base64`)
        .then((response) => {
          resolve(response.data);
        }).catch((error) => {
          reject(app.constructor.formatError(error));
        });
    });
  }

  saveSignature(dealId, documentId, { id, image, legal_disclaimer_id }) { // eslint-disable-line camelcase
    const app = this;
    return new Promise((resolve, reject) => {
      this.apiClient.post(`/customer-deals/${dealId}/documents/${documentId}/signature`, {
        signature_area_id: id,
        signature: image,
        legal_disclaimer_id,
      }).then((response) => {
        resolve(response.data);
      }).catch((error) => {
        reject(app.constructor.formatError(error));
      });
    });
  }

  static getCurrentUser() {
    const token = document.head.querySelector('[name="jwt-token"]').content;
    if (token) {
      try {
        const payload = decode(token);
        if (Object.prototype.hasOwnProperty.call(payload, 'user')) {
          return payload.user;
        }
        return null;
      } catch (e) {
        return null;
      }
    }
    return (token && Object.prototype.hasOwnProperty.call(token, 'user'))
      ? decode(token).user
      : null;
  }

  static formatError(error) {
    if (error.response && error.response.data && error.response.data.errors) {
      if (_.isArray(error.response.data.errors)) {
        return _.reduce(error.response.data.errors, (errorString, err) => {
          if (!_.find(errorString, (e) => e === err)) errorString.push(err);
          return errorString;
        }, []).join('<br/>');
      } return error.response.data.errors;
    }
    return error.toString();
  }
}

export default ApiService;
